import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "../../assets/editor.css";
import Loader from "../../utils/Loader";
import NotarizationSection from "./NotarizationSection";
import {
  ackTemplate,
  transmittalTemplate,
  secResTemplate,
  legalAdviceTemplate,
  selectStyles,
} from "../Constants";
import generateNewInvoiceTypePDF from "./GenerateNewInvoiceType";
import PdfPreviewModal from "../PreviewPdfModal";
import { useInvoiceContext } from "../../contexts/InvoiceContext";
import { useAdminContext } from "../../contexts/AdminContext";
import Select from "react-select";
import { doc, addDoc, updateDoc, collection } from "firebase/firestore";
import { db } from "../../firebase";
import { useUser } from "../../contexts/UserContext";
import ConfirmNavigationModal from "../ConfirmNavigationModal";
import SendInvoiceModal from "../Invoice/SendInvoiceModal";
import {
  mailForm,
  findExistingClient,
  hasClientDataChanged,
  generateNextCID,
  formatInvoiceNumber,
  updateLastInvoiceNumber,
  getLastInvoiceNumber,
  encryptInvoice,
} from "../../utils/Utility";
import { formatOldUrl, decryptData } from "../../utils/cloudApi";
import TableConfigSection from "./TableConfiguration";
import DynamicTable from "./DynamicTable";

const AdditionalInvoiceTypeForm = ({ setNavbarButtons }) => {
  // console.log("Re rendering");
  const location = useLocation();
  const { invoiceType, ackType } = location.state || {};
  const template = location.state?.template;
  const { setInvoices, invoices, templates, setTemplates } =
    useInvoiceContext();
  const { id } = useParams();
  const {
    user,
    invoiceClients,
    addInvoiceClient,
    updateInvoiceClient,
    privateKeyInMemory,
    getRecipients,
  } = useUser();

  const {
    addUserInvoice,
    updateUserInvoice,
    isViewingOtherUser,
    selectedUserData,
    adminData,
    selectedUserId,
    addUserInvoiceClient,
    updateUserInvoiceClient,
  } = useAdminContext();

  const navigationState = {
    adminContext: isViewingOtherUser
      ? {
          selectedUserId,
          isViewingOtherUser,
          selectedUserData,
        }
      : null,
  };

  const defaultType = useMemo(() => {
    switch (invoiceType) {
      case "ack":
        return "Acknowledgement";
      case "trans":
        return "Transmittal";
      case "secres":
        return "Secretarial Resolution";
      case "legal":
        return "Legal Advice";
      default:
        return "Document";
    }
  }, [invoiceType]);

  const currentUser = isViewingOtherUser ? selectedUserData : user;
  const currentInvoices = isViewingOtherUser
    ? adminData?.userInvoices || null
    : invoices;
  const currentInvoiceClients = isViewingOtherUser
    ? adminData?.userClients || null
    : invoiceClients;
  const currentUpdateClient = isViewingOtherUser
    ? updateUserInvoiceClient
    : updateInvoiceClient;
  const currentAddClient = isViewingOtherUser
    ? addUserInvoiceClient
    : addInvoiceClient;
  const baseTemplate = useMemo(() => {
    switch (invoiceType) {
      case "ack":
        return ackTemplate;
      case "trans":
        return transmittalTemplate;
      case "secres":
        return secResTemplate;
      case "legal":
        return legalAdviceTemplate;
      default:
        return ackTemplate;
    }
  }, [invoiceType]);
  const currentTemplates = isViewingOtherUser
    ? adminData?.userTemplates
    : templates;

  const isLoadingDataRef = useRef(false);
  const isNew = !id;
  const [isSendModalOpen, setIsSendModalOpen] = useState(false);
  const [headerImageDims, setHeaderImageDims] = useState({
    width: 0,
    height: 0,
  });

  const [footerImageDims, setFooterImageDims] = useState({
    width: 0,
    height: 0,
  });

  const [tableConfig, setTableConfig] = useState({
    includeTable: false,
    tableType:
      invoiceType === "ack" ? "item-price" : "document-description-copies",
    customColumns:
      invoiceType === "ack"
        ? [
            { key: "item", header: "Item", type: "text" },
            { key: "price", header: "Price", type: "currency" },
          ]
        : [
            { key: "document", header: "Document", type: "text" },
            { key: "description", header: "Description", type: "textarea" },
            { key: "copies", header: "No. of Copies", type: "number" },
          ],
  });

  const [isDataLoaded, setIsDataLoaded] = useState(false);

  const quillModules = {
    toolbar: [
      [{ header: [3, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      [{ align: [] }],
    ],
    keyboard: {
      bindings: {
        tab: {
          key: 9,
          handler: function (range) {
            this.quill.insertEmbed(
              range.index,
              "text",
              "\u00A0\u00A0\u00A0\u00A0"
            );
            return false;
          },
        },
      },
    },
  };

  const getInitialTemplate = useCallback(() => {
    const baseData = {
      ...baseTemplate,
      invoiceNo: "",
      tableIncluded: false,
      tableConfig: {
        includeTable: false,
        tableType:
          invoiceType === "ack" || invoiceType === "secres"
            ? "item-price"
            : "document-description-copies",
        customColumns:
          invoiceType === "ack" || invoiceType === "secres"
            ? [
                { key: "item", header: "Item", type: "text" },
                { key: "price", header: "Price", type: "currency" },
              ]
            : [
                { key: "document", header: "Document", type: "text" },
                { key: "description", header: "Description", type: "textarea" },
                { key: "copies", header: "No. of Copies", type: "number" },
              ],
      },
    };

    // Transmittal and Legal Advice don't need notarization
    if (invoiceType === "trans" || invoiceType === "legal") {
      return baseData;
    }
    // Acknowledgement and Sec-Res can have notarization
    else if (invoiceType === "ack" || invoiceType === "secres") {
      const ackData = {
        ...baseData,
        ackType: ackType,
      };

      if (ackType === "non-notarized") {
        return ackData;
      }

      // NEW: Simplified notarization structure with single content field
      return {
        ...ackData,
        notarization: {
          location: "Cebu City",
          date: baseData.date,
          content: "", // Single content field instead of notaryText and contentOrder
          notaryInfo: {
            docNo: "",
            pageNo: "",
            bookNo: "",
            series: "",
          },
          // Add resolution fields for sec-res type
          ...(invoiceType === "secres" && {
            resolutionNumber: "",
            resolutionYear: new Date().getFullYear().toString(),
          }),
        },
      };
    }

    return baseData;
  }, [ackType, baseTemplate, invoiceType]);

  const initialTemplate = getInitialTemplate();
  const [formData, setFormData] = useState(initialTemplate);
  const navigate = useNavigate();
  const [originalData, setOriginalData] = useState(formData);
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [headerImageDataUrl, setHeaderImageDataUrl] = useState(null);
  const [footerImageDataUrl, setFooterImageDataUrl] = useState(null);
  const [showPrevieModal, setShowPreviewModal] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");

  // Modified useEffect
  useEffect(() => {
    const loadTemplate = async () => {
      // console.log(template);
      if (template && isNew && !isLoadingDataRef.current) {
        isLoadingDataRef.current = true;

        const decrypted = await decryptData(
          template,
          user.uid,
          privateKeyInMemory
        );

        // console.log(decrypted);

        const tempId = decrypted.id ? decrypted.id : decrypted.templateRef;
        const temp =
          tempId && currentTemplates
            ? currentTemplates.find((tpl) => tpl.id === tempId)
            : null;
        const tempType = temp?.tempType ? temp?.tempType : "ack";

        const defaultData = getInitialTemplate();
        const invoiceNumber = Number(decrypted.lastInvoiceNumber ?? "");
        const formattedInvoiceNo = formatInvoiceNumber(invoiceNumber, tempType);
        // USE: Use the date from template if provided, otherwise use default
        const currentDate = decrypted.date || defaultData.date;

        // const notarizationToUse =
        //   ackType === "jurat" ? juratTemplate : notarizationTemplate;

        let loadedData = {
          ...defaultData,
          header: decrypted.header,
          headerImage: decrypted.logo || null,
          invoiceNo: formattedInvoiceNo,
          logo: decrypted.logo || null,
          footer: decrypted.footer || null,
          date: currentDate,
          from: { ...decrypted.from },
          items: { ...defaultData.items },
          pdfUrl: decrypted.pdfUrl || null,
          templateRef: decrypted.id ? decrypted.id : decrypted.templateRef,
          body1: decrypted.body1 || "",
          body2: decrypted.body2 || "",
          tableIncluded: decrypted.tableIncluded === true,
        };

        if (invoiceType === "ack" || invoiceType === "secres") {
          loadedData.notarizationStatus = decrypted.notarizationStatus;
          loadedData.notarizationType = decrypted.notarizationType;

          if (ackType && ackType !== "non-notarized") {
            loadedData = {
              ...loadedData,
              ackType: ackType,
              notarization: {
                location: "Cebu City",
                date: currentDate,
                content: "",
                notaryInfo: {
                  docNo: "",
                  pageNo: "",
                  bookNo: "",
                  series: "",
                },
                ...(invoiceType === "secres" && {
                  resolutionNumber: "",
                  resolutionYear: new Date().getFullYear().toString(),
                }),
              },
            };
          }
        }

        // Set table config from template
        if (decrypted.tableConfig) {
          setTableConfig(decrypted.tableConfig);
          loadedData.tableConfig = decrypted.tableConfig;
        } else {
          const defaultTableConfig = {
            includeTable: false,
            tableType:
              invoiceType === "ack"
                ? "item-price"
                : "document-description-copies",
            customColumns:
              invoiceType === "ack"
                ? [
                    { key: "item", header: "Item", type: "text" },
                    { key: "price", header: "Price", type: "currency" },
                  ]
                : [
                    { key: "document", header: "Document", type: "text" },
                    {
                      key: "description",
                      header: "Description",
                      type: "textarea",
                    },
                    { key: "copies", header: "No. of Copies", type: "number" },
                  ],
          };
          setTableConfig(defaultTableConfig);
          loadedData.tableIncluded = false;
          loadedData.tableConfig = defaultTableConfig;
        }

        setHeaderImageDataUrl(formatOldUrl(decrypted.logo) || null);
        setFooterImageDataUrl(formatOldUrl(decrypted.footer || null));

        setFormData(loadedData);
        setOriginalData(JSON.parse(JSON.stringify(loadedData)));

        // console.log(loadedData);
        // Reset the ref after a brief delay
        setTimeout(() => {
          setIsDataLoaded(true);
          isLoadingDataRef.current = false;
        }, 200);
      }
    };

    loadTemplate();
  }, [
    template,
    isNew,
    currentTemplates,
    invoiceType,
    getInitialTemplate,
    ackType,
    privateKeyInMemory,
    user,
  ]);

  const handleChange = (e, field, subfield) => {
    if (subfield) {
      setFormData((prev) => ({
        ...prev,
        [field]: { ...prev[field], [subfield]: e.target.value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [field]: e.target.value }));
    }
  };

  const handleClosingChange = useCallback((e, field) => {
    setFormData((prev) => ({
      ...prev,
      closing: {
        ...prev.closing,
        [field]: e.target.value,
      },
    }));
  }, []);

  // React select
  const clientOptions = useMemo(() => {
    if (!currentInvoiceClients) return [{ value: "", label: "New Client" }];

    return [
      { value: "", label: "New Client" },
      ...currentInvoiceClients.map((client) => ({
        value: client.id,
        label: `${client.name}`,
      })),
    ];
  }, [currentInvoiceClients]);

  const selectedOption = useMemo(() => {
    if (!formData.to.name || formData.to.name.trim() === "") {
      return { value: "", label: "New Client" };
    }

    const foundClient = currentInvoiceClients?.find(
      (client) =>
        client.name.trim().toLowerCase() ===
        formData.to.name.trim().toLowerCase()
    );

    return foundClient
      ? {
          value: foundClient.id,
          label: `${foundClient.name}`,
        }
      : { value: "", label: "New Client" };
  }, [currentInvoiceClients, formData.to.name]);

  const handleClientSelect = (selectedOption) => {
    if (!selectedOption || selectedOption.value === "") {
      // Clear all fields when "New Client" is selected
      setFormData((prev) => ({
        ...prev,
        to: {
          name: "",
          tinNo: "",
          address: "",
          email: "",
          contact: "",
          rep: "",
        },
      }));
      return;
    }

    // Find the selected client
    const selectedClient = currentInvoiceClients.find(
      (client) => client.id === selectedOption.value
    );

    if (selectedClient) {
      setFormData((prev) => ({
        ...prev,
        to: {
          name: selectedClient.name || "",
          tinNo: selectedClient.tinNo || "",
          address: selectedClient.address || "",
          email: selectedClient.email || "",
          contact: selectedClient.contact || "",
          rep: selectedClient.rep || "",
        },
      }));
    }
  };

  const hasUnsavedChanges = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(originalData);
  }, [formData, originalData]);

  const handleNavigate = useCallback(
    (uid) => {
      if (invoiceType === "ack") {
        navigate(`/edit-doc/${uid}`, {
          state: { invoiceType: "ack", ackType: ackType || "acknowledgement" },
        });
      } else if (invoiceType === "trans") {
        navigate(`/edit-doc/${uid}`, {
          state: {
            invoiceType: "trans",
            ackType: ackType || "acknowledgement",
          },
        });
      } else if (invoiceType === "legal") {
        navigate(`/edit-doc/${uid}`, {
          state: {
            invoiceType: "legal",
            ackType: ackType || "acknowledgement",
          },
        });
      } else if (invoiceType === "secres") {
        navigate(`/edit-doc/${uid}`, {
          state: {
            invoiceType: "secres",
            ackType: ackType || "acknowledgement",
          },
        });
      }
    },
    [invoiceType, navigate, ackType]
  );

  // Format date
  const formattedDate = new Date(formData.date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  // Helper function to save new client or update existing one - updated for admin mode
  const saveOrUpdateClient = useCallback(
    async (clientData) => {
      try {
        if (!clientData.name || clientData.name.trim() === "") {
          return null; // Don't save empty clients
        }

        const existingClient = findExistingClient(
          clientData,
          currentInvoiceClients
        );

        const clientToSave = {
          name: clientData.name.trim(),
          address: clientData.address?.trim() || "",
          tinNo: clientData.tinNo?.trim() || "",
          contact: clientData.contact?.trim() || "",
          rep: clientData.rep?.trim() || "",
          email: clientData.email?.trim() || "",
        };

        if (existingClient) {
          // Check if any data has changed
          if (hasClientDataChanged(clientData, existingClient)) {
            // Update existing client (preserve existing CID)
            const targetUserId = isViewingOtherUser
              ? selectedUserId
              : currentUser.uid;
            const clientRef = doc(
              db,
              "users",
              targetUserId,
              "invoiceClients",
              existingClient.id
            );
            await updateDoc(clientRef, {
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            });

            const updatedClient = {
              id: existingClient.id,
              cid: existingClient.cid,
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            };

            // Update the state with the updated client data using appropriate method
            currentUpdateClient(existingClient.id, {
              ...clientToSave,
              updatedAt: new Date().toISOString(),
            });

            toast.info("Client information updated.");
            return updatedClient;
          } else {
            return existingClient;
          }
        } else {
          // Create new client with generated CID
          const newCID = generateNextCID(currentInvoiceClients);
          const targetUserId = isViewingOtherUser
            ? selectedUserId
            : currentUser.uid;

          const newClientData = {
            ...clientToSave,
            cid: newCID,
            createdAt: new Date().toISOString(),
          };

          const clientRef = await addDoc(
            collection(db, "users", targetUserId, "invoiceClients"),
            newClientData
          );

          const savedClient = { id: clientRef.id, ...newClientData };

          // Add the new client to the state using appropriate method
          currentAddClient(savedClient);
          toast.success(`New client added to your database (${newCID}).`);
          return savedClient;
        }
      } catch (error) {
        console.error("Error saving/updating client:", error);
        toast.warn(
          "Invoice saved, but failed to save/update client information."
        );
        return null;
      }
    },
    [
      currentInvoiceClients,
      currentUser,
      currentAddClient,
      currentUpdateClient,
      isViewingOtherUser,
      selectedUserId,
    ]
  );

  const handleRichTextChange = (field, content) => {
    const sanitizedContent = content
      .replace(/\t/g, "&nbsp;&nbsp;&nbsp;&nbsp;")
      .replace(/ {2}/g, "&nbsp;&nbsp;");

    setFormData((prev) => {
      if (prev[field] === sanitizedContent) return prev;
      return { ...prev, [field]: sanitizedContent };
    });
  };

  const handleSubmit = useCallback(async () => {
    if (!hasUnsavedChanges) {
      toast.info(`No changes to save.`);
      return;
    }

    // console.log(formData);

    try {
      setLoading(true);
      setLoadingText(`Saving ${formData.header}...`);

      if (formData.to.name && formData.to.name.trim() !== "") {
        setLoadingText("Processing client information...");
        await saveOrUpdateClient(formData.to);
      }

      let updatedFormData = { ...formData, createdBy: currentUser.uid };

      setLoadingText("Preparing PDF...");
      const curClient = findExistingClient(formData.to, currentInvoiceClients);
      const tempCid = curClient?.cid || null;

      const prepPdfUrl = await generateNewInvoiceTypePDF(
        formData,
        headerImageDataUrl,
        footerImageDataUrl,
        tempCid,
        formattedDate,
        invoiceType,
        ""
      );
      updatedFormData.pdfUrl = prepPdfUrl;

      // Encrypt for database storage
      let encryptedInvoice;
      try {
        setLoadingText(`Encrypting ${updatedFormData.header}...`);
        const recipients = await getRecipients();
        encryptedInvoice = await encryptInvoice(updatedFormData, recipients);
      } catch (error) {
        console.error("Encryption error:", error);
        throw new Error("Failed to encrypt data");
      }

      // Save ENCRYPTED data to database
      let docRef;
      if (isNew) {
        docRef = await addDoc(collection(db, "invoices"), encryptedInvoice);
        toast.success(`${updatedFormData.header} created successfully!`);
      } else {
        docRef = doc(db, "invoices", id);
        await updateDoc(docRef, encryptedInvoice);
        toast.success(`${updatedFormData.header} updated successfully!`);
      }

      if (isNew && updatedFormData.templateRef) {
        try {
          await updateLastInvoiceNumber(updatedFormData.templateRef);
          setTemplates((prevTemplates) =>
            prevTemplates.map((template) =>
              template.id === updatedFormData.templateRef
                ? {
                    ...template,
                    lastInvoiceNumber:
                      Number(template.lastInvoiceNumber || 0) + 1,
                  }
                : template
            )
          );
        } catch (templateError) {
          console.error("Error updating template:", templateError);
        }
      }

      // Store DECRYPTED data in state
      const stateData = { ...updatedFormData, id: isNew ? docRef.id : id };

      if (isViewingOtherUser) {
        if (isNew) {
          addUserInvoice(stateData);
        } else {
          updateUserInvoice(id, updatedFormData);
        }
      } else {
        setInvoices((prevInvoices) =>
          isNew
            ? [...prevInvoices, stateData]
            : prevInvoices.map((invoice) =>
                invoice.id === id ? { id, ...updatedFormData } : invoice
              )
        );
      }

      setOriginalData(updatedFormData);

      if (isNew) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        handleNavigate(docRef.id);
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      toast.error("Failed to save invoice. Please try again.");
    } finally {
      setLoading(false);
      setLoadingText("");
    }
  }, [
    formData,
    handleNavigate,
    id,
    isNew,
    setInvoices,
    currentInvoiceClients,
    isViewingOtherUser,
    addUserInvoice,
    updateUserInvoice,
    currentUser,
    hasUnsavedChanges,
    formattedDate,
    headerImageDataUrl,
    footerImageDataUrl,
    invoiceType,
    getRecipients,
    saveOrUpdateClient,
    setTemplates,
  ]);

  const closeModal = () => {
    setShowPreviewModal(false);
  };

  const cancelNavigation = () => {
    setIsConfirmModalOpen(false);
  };

  const confirmNavigation = useCallback(() => {
    setIsConfirmModalOpen(false);
    if (invoiceType === "ack") {
      navigate("/receipts/acknowledgement");
    } else if (invoiceType === "trans") {
      navigate("/records/transmittal");
    } else if (invoiceType === "legal") {
      navigate("/records/legal-advice");
    } else if (invoiceType === "secres") {
      navigate("/records/sec-res");
    }
  }, [navigate, invoiceType]);

  const handlePreview = useCallback(
    async (action) => {
      setLoading(true);
      setLoadingText("Preparing PDF...");

      // console.log(formData);
      try {
        const pdfUrl = await generateNewInvoiceTypePDF(
          formData,
          headerImageDataUrl,
          footerImageDataUrl,
          "",
          formattedDate,
          invoiceType,
          action
        );
        setPdfUrl(pdfUrl);
        setShowPreviewModal(true);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setLoadingText("");
      }
    },
    [
      headerImageDataUrl,
      footerImageDataUrl,
      formData,
      formattedDate,
      invoiceType,
    ]
  );

  const handleGeneratePdf = useCallback(
    async (action) => {
      setLoading(true);
      setLoadingText("Downloading PDF...");
      try {
        const curClient = findExistingClient(
          formData.to,
          currentInvoiceClients
        );
        const tempCid = curClient?.cid || null;

        const pdfUrl = await generateNewInvoiceTypePDF(
          formData,
          headerImageDataUrl,
          footerImageDataUrl,
          tempCid,
          formattedDate,
          invoiceType,
          action
        );

        setPdfUrl(pdfUrl);
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
        setLoadingText("");
      }
    },
    [
      headerImageDataUrl,
      footerImageDataUrl,
      currentInvoiceClients,
      formData,
      formattedDate,
      invoiceType,
    ]
  );

  const handleGoBack = useCallback(() => {
    // console.log(invoiceType);
    if (hasUnsavedChanges) {
      setIsConfirmModalOpen(true);
    } else {
      if (invoiceType === "ack") {
        navigate("/receipts/acknowledgement");
      } else if (invoiceType === "trans") {
        navigate("/records/transmittal");
      } else if (invoiceType === "legal") {
        navigate("/records/legal-advice");
      } else if (invoiceType === "secres") {
        navigate("/records/sec-res");
      }
    }
  }, [navigate, invoiceType, hasUnsavedChanges]);

  const blobUrlToBase64 = async (blobUrl) => {
    const response = await fetch(blobUrl);
    const blob = await response.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  const handleSendEmail = async ({ subject, body, recipient }) => {
    setLoading(true);
    setLoadingText("Sending. Please wait");

    const emailBody = mailForm(body);

    try {
      const curClient = findExistingClient(formData.to, currentInvoiceClients);
      const tempCid = curClient?.cid || "CID000XX";

      const formattedFilename = `${tempCid}_${formData.invoiceNo}.pdf`;

      let attachment = {
        fileName: `${formattedFilename}`,
      };

      // generate the pdf first
      const prepPdfUrl = await generateNewInvoiceTypePDF(
        formData,
        formData.headerImage,
        formData.footer,
        formattedDate,
        invoiceType,
        ""
      );

      attachment.fileData = await blobUrlToBase64(prepPdfUrl);

      const response = await fetch(
        `${process.env.REACT_APP_FIREBASE_BASE_API}/sendEmail`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            data: {
              to: recipient.trim(),
              subject: subject.trim(),
              message: emailBody,
              attachment: attachment,
            },
          }),
        }
      );
      if (!response.ok) {
        throw new Error("Server error. Please try again.");
      }

      const result = await response.json();
      toast.success(result.data?.message || "Email sent successfully!");
    } catch (error) {
      console.error("Error sending email:", error);
      toast.error("Failed to send email. Please try again.");
    } finally {
      setIsSendModalOpen(false);
      setLoading(false);
      setLoadingText("");
    }
  };

  // Filter invoices to exclude those with invoiceNo starting with "TL" or "AR"
  const filteredInvoices = useMemo(() => {
    if (!currentInvoices || currentInvoices.length === 0) return [];

    const prefixMap = {
      ack: "AR",
      trans: "TL",
      secres: "SR",
      legal: "LA",
    };

    const prefix = prefixMap[invoiceType];

    if (prefix) {
      return currentInvoices.filter(
        (invoice) => invoice.invoiceNo && invoice.invoiceNo.startsWith(prefix)
      );
    }

    return [];
  }, [currentInvoices, invoiceType]);

  // Find the index of the current invoice in the filtered list
  const currentInvoiceIndex = useMemo(() => {
    if (!id || filteredInvoices.length === 0) return -1;
    return filteredInvoices.findIndex((invoice) => invoice.id === id);
  }, [id, filteredInvoices]);

  // Navigation handlers using the filteredInvoices array
  const handleNextInvoice = () => {
    if (
      currentInvoiceIndex !== -1 &&
      currentInvoiceIndex < filteredInvoices.length - 1
    ) {
      // Navigate to the next invoice
      const nextInvoiceId = filteredInvoices[currentInvoiceIndex + 1].id;
      navigate(`/edit-doc/${nextInvoiceId}`, {
        state: {
          invoiceType: invoiceType,
          ackType: ackType,
          ...navigationState,
        },
      });
    }
  };

  const handlePreviousInvoice = () => {
    if (currentInvoiceIndex > 0) {
      // Navigate to the previous invoice
      const previousInvoiceId = filteredInvoices[currentInvoiceIndex - 1].id;
      navigate(`/edit-doc/${previousInvoiceId}`, {
        state: {
          invoiceType: invoiceType,
          ackType: ackType,
          ...navigationState,
        },
      });
    }
  };

  const newInvoiceHandler = useCallback(async () => {
    if (hasUnsavedChanges) {
      toast.info(
        "Please save your changes before creating a new one to ensure all modifications are applied correctly"
      );
      return;
    }

    let lastInvoiceNumber = 0;

    if (formData.templateRef) {
      const fetchedInvoiceNumber = await getLastInvoiceNumber(
        formData.templateRef
      );
      if (fetchedInvoiceNumber !== null && fetchedInvoiceNumber !== undefined) {
        lastInvoiceNumber = fetchedInvoiceNumber;
        toast.info(
          `New ${defaultType} ${formatInvoiceNumber(
            lastInvoiceNumber,
            invoiceType
          )} is loaded!`
        );
      } else {
        toast.info(
          `Unable to locate the ${defaultType} template. The system will assign #1 as the starting number.`
        );
        lastInvoiceNumber = 0;
      }
    } else {
      toast.info(
        `Unable to locate the ${defaultType} template. The system will assign #1 as the starting number.`
      );
      lastInvoiceNumber = 0;
    }

    // Get the proper initial template with correct structure
    const freshTemplate = getInitialTemplate();

    // Find the actual template object
    const encryptedTemplate = currentTemplates?.find(
      (t) => t.id === formData.templateRef
    );

    // DECRYPT the template
    let actualTemplate = null;
    if (encryptedTemplate) {
      try {
        actualTemplate = await decryptData(
          encryptedTemplate,
          user.uid,
          privateKeyInMemory
        );
      } catch (error) {
        console.error("Error decrypting template:", error);
        toast.error("Failed to load template data");
      }
    }

    // Ensure we have the current date for notarization
    const currentDate = new Date().toISOString().split("T")[0];

    // Create template data with proper structure
    const templateData = {
      templateRef: formData.templateRef,
      header: actualTemplate?.header || baseTemplate.header,
      logo: actualTemplate?.logo || formData.headerImage,
      footer: actualTemplate?.footer || formData.footer,
      from: { ...(actualTemplate?.from || formData.from) },
      to: {
        name: "",
        tinNo: "",
        address: "",
        email: "",
        contact: "",
        rep: "",
      },
      date: currentDate,
      items: { ...freshTemplate.items },
      body1: actualTemplate?.body1 || freshTemplate.body1 || "",
      body2: actualTemplate?.body2 || freshTemplate.body2 || "",
      subject: "",
      closing: { ...freshTemplate.closing },
      lastInvoiceNumber: lastInvoiceNumber,
      tableIncluded: actualTemplate?.tableIncluded || false,
      tableConfig: actualTemplate?.tableConfig || tableConfig,
      pdfUrl: null,
      // NEW: Simplified notarization data structure
      ...((invoiceType === "ack" || invoiceType === "secres") &&
        ackType &&
        ackType !== "non-notarized" && {
          notarization: {
            location: actualTemplate?.notarization?.location || "Cebu City",
            date: currentDate,
            content: actualTemplate?.notarization?.content || "", // Single content field
            notaryInfo: {
              docNo: actualTemplate?.notarization?.notaryInfo?.docNo || "",
              pageNo: actualTemplate?.notarization?.notaryInfo?.pageNo || "",
              bookNo: actualTemplate?.notarization?.notaryInfo?.bookNo || "",
              series: actualTemplate?.notarization?.notaryInfo?.series || "",
            },
            // Add resolution fields for sec-res type
            ...(invoiceType === "secres" && {
              resolutionNumber:
                actualTemplate?.notarization?.resolutionNumber || "",
              resolutionYear:
                actualTemplate?.notarization?.resolutionYear ||
                new Date().getFullYear().toString(),
            }),
          },
          ackType: ackType,
        }),
    };

    if (invoiceType === "ack" || invoiceType === "secres") {
      templateData.notarizationStatus = actualTemplate?.notarizationStatus;
      templateData.notarizationType = actualTemplate?.notarizationType;
    }

    const navigationState = {
      adminContext: isViewingOtherUser
        ? {
            selectedUserId,
            isViewingOtherUser,
            selectedUserData,
          }
        : null,
    };

    const finalState = {
      invoiceType: invoiceType,
      ackType: ackType,
      template: templateData,
      ...navigationState,
    };

    // Reset the loading ref before navigation
    isLoadingDataRef.current = false;

    navigate("/new-doc", { state: finalState });
  }, [
    currentTemplates,
    hasUnsavedChanges,
    baseTemplate,
    isViewingOtherUser,
    selectedUserData,
    selectedUserId,
    formData,
    navigate,
    defaultType,
    invoiceType,
    tableConfig,
    getInitialTemplate,
    ackType,
    privateKeyInMemory,
    user,
  ]);

  const buttonConfig = useMemo(() => {
    const cfg = [
      {
        onClick: handleGoBack,
        icon: "arrow_back",
        label: "Go back",
        className: "text-xs",
      },
      ...(!isNew
        ? [
            {
              onClick: newInvoiceHandler,
              icon: "note_add",
              label: `New ${defaultType}`,
              className: "text-xs",
            },
          ]
        : []),
      {
        onClick: () => handleGeneratePdf("download"),
        icon: "download",
        label: "Download PDF",
        className: "text-xs",
      },
      {
        onClick: () => handlePreview("preview"),
        icon: "visibility",
        label: "Preview PDF",
        className: "text-xs",
      },
      {
        onClick: () => {
          if (hasUnsavedChanges) {
            toast.info("Please save your changes first to send invoice");
            return;
          }
          setIsSendModalOpen(true);
        },
        icon: "email",
        label: "Send Email",
        className: "text-xs",
      },
      {
        onClick: handleSubmit,
        icon: "save",
        label: "Save PDF",
        className: "text-xs",
      },
    ];
    return cfg;
  }, [
    defaultType,
    isNew,
    newInvoiceHandler,
    handleGeneratePdf,
    handleSubmit,
    handlePreview,
    handleGoBack,
    hasUnsavedChanges,
  ]);

  useEffect(() => {
    setNavbarButtons(buttonConfig);
    return () => setNavbarButtons([]);
  }, [setNavbarButtons, buttonConfig]);

  useEffect(() => {
    if (!isNew && !isLoadingDataRef.current) {
      isLoadingDataRef.current = true;

      const invoiceFromState = currentInvoices.find((inv) => inv.id === id);

      if (invoiceFromState) {
        const loadDocument = async () => {
          try {
            const decrypted = await decryptData(
              invoiceFromState,
              user.uid,
              privateKeyInMemory
            );

            setHeaderImageDataUrl(formatOldUrl(decrypted.headerImage) || null);
            setFooterImageDataUrl(formatOldUrl(decrypted.footer) || null);

            if (decrypted?.tableConfig) {
              setTableConfig(decrypted.tableConfig);
            }

            let mergedData = {
              ...decrypted,
              ...(decrypted.body1 === null || decrypted.body1 === undefined
                ? { body1: "" }
                : {}),
              ...(decrypted.body2 === null || decrypted.body2 === undefined
                ? { body2: "" }
                : {}),
              from: decrypted?.from || ackTemplate.from,
              to: {
                name: decrypted.to?.name || "",
                tinNo: decrypted.to?.tinNo || "",
                address: decrypted.to?.address || "",
                email: decrypted.to?.email || "",
                contact: decrypted.to?.contact || "",
                rep: decrypted.to?.rep || "",
              },
              tableIncluded: decrypted.tableIncluded === true,
              tableConfig: decrypted.tableConfig || {
                includeTable: decrypted.tableIncluded === true,
                tableType:
                  invoiceType === "ack"
                    ? "item-price"
                    : "document-description-copies",
                customColumns:
                  invoiceType === "ack"
                    ? [
                        { key: "item", header: "Item", type: "text" },
                        { key: "price", header: "Price", type: "currency" },
                      ]
                    : [
                        { key: "document", header: "Document", type: "text" },
                        {
                          key: "description",
                          header: "Description",
                          type: "textarea",
                        },
                        {
                          key: "copies",
                          header: "No. of Copies",
                          type: "number",
                        },
                      ],
              },
            };

            if (invoiceType === "ack" || invoiceType === "secres") {
              const currentAckType =
                decrypted.ackType || ackType || "acknowledgement";
              mergedData.ackType = currentAckType;

              if (decrypted.notarization) {
                // Keep existing notarization data as-is
                mergedData.notarization = {
                  ...decrypted.notarization,
                  // Ensure content exists
                  content: decrypted.notarization.content || "",
                };
              } else if (currentAckType !== "non-notarized") {
                // Create new notarization structure for old documents
                mergedData.notarization = {
                  location: "Cebu City",
                  date: mergedData.date,
                  content: "",
                  notaryInfo: {
                    docNo: "",
                    pageNo: "",
                    bookNo: "",
                    series: "",
                  },
                  ...(invoiceType === "secres" && {
                    resolutionNumber: "",
                    resolutionYear: new Date().getFullYear().toString(),
                  }),
                };
              }
            }

            // console.log(mergedData);
            setFormData(mergedData);
            setOriginalData(mergedData);

            setTimeout(() => {
              setIsDataLoaded(true);
              isLoadingDataRef.current = false;
            }, 200);
          } catch (err) {
            console.error("Error decrypting:", err);
            toast.error("Failed to decrypt!");
            isLoadingDataRef.current = false;
          }
        };

        loadDocument();
      } else {
        // console.log("Invoice not found in state, may still be loading");
        setTimeout(() => {
          isLoadingDataRef.current = false;
        }, 100);
      }
    }
  }, [
    id,
    isNew,
    currentInvoices,
    invoiceType,
    ackType,
    user.uid,
    privateKeyInMemory,
  ]);

  useEffect(() => {
    // Only sync if we're not currently loading data and there's a mismatch
    if (
      !isLoadingDataRef.current &&
      formData.tableConfig &&
      (tableConfig.includeTable !== formData.tableIncluded ||
        tableConfig.tableType !== formData.tableConfig?.tableType)
    ) {
      setTableConfig({
        ...formData.tableConfig,
        includeTable: formData.tableIncluded === true,
      });
    }
  }, [
    formData.tableIncluded,
    formData.tableConfig,
    tableConfig.includeTable,
    tableConfig.tableType,
  ]);

  const handleNotarizationChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      notarization: {
        ...prev.notarization,
        [field]: value,
      },
    }));
  }, []);

  const handleNotaryInfoChange = useCallback((field, value) => {
    setFormData((prev) => ({
      ...prev,
      notarization: {
        ...prev.notarization,
        notaryInfo: {
          ...prev.notarization.notaryInfo,
          [field]: value,
        },
      },
    }));
  }, []);

  const handleAffiantChange = useCallback((index, field, value) => {
    setFormData((prev) => {
      const updatedAffiants = [...prev.notarization.affiants];
      updatedAffiants[index] = { ...updatedAffiants[index], [field]: value };
      return {
        ...prev,
        notarization: {
          ...prev.notarization,
          affiants: updatedAffiants,
        },
      };
    });
  }, []);

  const handleWitnessChange = useCallback((index, field, value) => {
    setFormData((prev) => {
      const updatedWitnesses = [...prev.notarization.witnesses];
      updatedWitnesses[index] = { ...updatedWitnesses[index], [field]: value };
      return {
        ...prev,
        notarization: {
          ...prev.notarization,
          witnesses: updatedWitnesses,
        },
      };
    });
  }, []);

  const addAffiant = () => {
    setFormData((prev) => ({
      ...prev,
      notarization: {
        ...prev.notarization,
        affiants: [
          ...(prev.notarization.affiants || []),
          { name: "", label: "Affiant" },
        ],
      },
    }));
  };

  const removeAffiant = (index) => {
    const updatedAffiants = formData.notarization.affiants.filter(
      (_, i) => i !== index
    );
    setFormData((prev) => ({
      ...prev,
      notarization: {
        ...prev.notarization,
        affiants: updatedAffiants,
      },
    }));
  };

  const addWitness = () => {
    setFormData((prev) => ({
      ...prev,
      notarization: {
        ...prev.notarization,
        witnesses: [
          ...(prev.notarization?.witnesses || []),
          { name: "", label: "Witness" },
        ],
      },
    }));
  };

  const removeWitness = (index) => {
    const updatedWitnesses = formData.notarization.witnesses.filter(
      (_, i) => i !== index
    );
    setFormData((prev) => ({
      ...prev,
      notarization: {
        ...prev.notarization,
        witnesses: updatedWitnesses,
      },
    }));
  };

  const pageTitle = useMemo(() => {
    switch (invoiceType) {
      case "ack":
        return "Acknowledgement Receipt";
      case "trans":
        return "Transmittal Letter";
      case "secres":
        return "Secretarial Resolution";
      case "legal":
        return "Legal Advice";
      default:
        return "Document";
    }
  }, [invoiceType]);

  return (
    <>
      <Helmet>
        <title>UP-Hub Tools - {pageTitle}</title>
      </Helmet>

      <div className="p-6 bg-inherit">
        {loading && <Loader text={loadingText} />}

        {!isNew && (
          <div className="flex flex-col fixed top-42 right-0 py-4 px-2 space-y-3 z-20 bg-tier-primary dark:bg-tier-primary-dark dark:border-gray-600 bg-opacity-70 shadow-lg rounded-lg">
            {/* Back */}
            <div className="relative group">
              <button
                onClick={handlePreviousInvoice}
                className="btn-floating focus:ring-opacity-50 "
              >
                <span className="material-symbols-outlined">chevron_left</span>
              </button>
              <span className="whitespace-nowrap absolute right-full top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 text-color bg-gray-600 p-2 rounded text-sm transition-opacity duration-300">
                Previous
              </span>
            </div>

            <div className="relative group border-y">
              <div className="text-sm">
                {currentInvoiceIndex !== -1 ? currentInvoiceIndex + 1 : 0} of{" "}
                {filteredInvoices.length}
              </div>
            </div>

            {/* Next */}
            <div className="relative group">
              <button
                onClick={handleNextInvoice}
                className="btn-floating focus:ring-opacity-50 "
              >
                <span className="material-symbols-outlined">chevron_right</span>
              </button>
              <span className="whitespace-nowrap absolute right-full top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 text-color bg-gray-600 p-2 rounded text-sm transition-opacity duration-300">
                Next
              </span>
            </div>
          </div>
        )}

        {showPrevieModal && (
          <PdfPreviewModal
            invoiceData={null}
            pdfUrl={pdfUrl}
            onClose={closeModal}
          />
        )}

        {isSendModalOpen && (
          <SendInvoiceModal
            isOpen={isSendModalOpen}
            onClose={() => setIsSendModalOpen(false)}
            onSend={handleSendEmail}
            invoice={formData}
            defaultSubject={`${formData.closing.name} - ${formData.header} [ #${formData.invoiceNo} ]`}
            invoiceType={
              invoiceType === "ack" ? "acknowledgement" : "transmittal"
            }
          />
        )}

        <ConfirmNavigationModal
          isOpen={isConfirmModalOpen}
          onCancel={cancelNavigation}
          onConfirm={confirmNavigation}
          message="You have unsaved changes. Are you sure you want to leave without saving?"
        />

        <div className="form-section max-w-6xl mx-auto">
          <form>
            {/* Header & Date */}
            <div className="flex flex-col">
              {invoiceType === "ack" && (
                <span className="text-xs uppercase text-tier-secondary">
                  {formData.ackType === "non-notarized" ? "" : "Notarized | "}
                  {formData.ackType}
                </span>
              )}

              <span className="text-xs text-alt-color">
                {formData.invoiceNo}
              </span>
            </div>

            <div className="border-b-2 border-tier-secondary my-4"></div>

            <div className="mb-6 text-center">
              <label htmlFor="header" className="block text-xs">
                Document Title
              </label>
              <input
                id="header"
                type="text"
                value={formData.header || ""}
                readOnly
                className="text-2xl px-4 py-2 font-bold text-color bg-white/20 rounded-lg text-center w-full focus:outline-none"
                placeholder="Enter Header"
              />
            </div>

            {/* Header Image */}
            <div className="m-4 flex flex-col items-center justify-center">
              {headerImageDataUrl && (
                <>
                  <span className="block text-xs">Header Image</span>
                  <img
                    src={formatOldUrl(headerImageDataUrl) || null}
                    alt="Header"
                    className="border max-h-32 h-auto max-w-lg border-gray-300 dark:border-gray-600"
                    onLoad={(e) =>
                      setHeaderImageDims({
                        width: e.currentTarget.naturalWidth,
                        height: e.currentTarget.naturalHeight,
                      })
                    }
                  />
                  <p className="mt-1 text-xs text-center text-gray-200">
                    Dimensions: {headerImageDims.width}px ×{" "}
                    {headerImageDims.height}px
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="m-4 flex flex-col items-center justify-center">
              {footerImageDataUrl && (
                <>
                  <span className="block text-xs">Footer Image</span>
                  <img
                    src={formatOldUrl(footerImageDataUrl) || null}
                    alt="Footer"
                    className="border max-h-32 h-auto max-w-lg border-gray-300 dark:border-gray-600"
                    onLoad={(e) =>
                      setFooterImageDims({
                        width: e.currentTarget.naturalWidth,
                        height: e.currentTarget.naturalHeight,
                      })
                    }
                  />
                  <p className="mt-1 text-xs text-center text-gray-200">
                    Dimensions: {footerImageDims.width}px ×{" "}
                    {footerImageDims.height}px
                  </p>
                </>
              )}
            </div>

            <div className="border-b-2 border-tier-secondary pb-4"></div>

            <div className="m-4 grid grid-cols-4 gap-2">
              <div className="col-span-1">
                <label htmlFor="date" className="block text-xs">
                  Date
                </label>
                <input
                  id="date"
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleChange(e, "date")}
                  className="block custom-input"
                />
              </div>
              <div className="col-span-3">
                <label htmlFor="subject" className="block text-xs">
                  Subject/InRe
                </label>
                <input
                  id="subject"
                  type="text"
                  value={formData.subject || ""}
                  onChange={(e) => handleChange(e, "subject")}
                  className="w-full custom-input px-4 py-2 border rounded"
                  placeholder="Enter Subject or InRe"
                />
              </div>
            </div>

            <div className="border-b-2 border-tier-secondary pb-4"></div>

            <div className="m-4">
              <h2 className="text-xl font-bold mb-3">Issuing Company</h2>
              <label htmlFor="from-name" className="block text-sm">
                Name
              </label>
              <input
                id="from-name"
                type="text"
                value={formData.from.name || ""}
                onChange={(e) => handleChange(e, "from", "name")}
                className="block custom-input mb-3"
              />
              <label htmlFor="from-address" className="block text-sm">
                Address
              </label>
              <input
                id="from-address"
                type="text"
                value={formData.from.address || ""}
                onChange={(e) => handleChange(e, "from", "address")}
                className="block custom-input mb-3"
              />
              <label htmlFor="from-contact" className="block text-sm">
                Contact
              </label>
              <input
                id="from-contact"
                type="text"
                value={formData.from.contact || ""}
                onChange={(e) => handleChange(e, "from", "contact")}
                className="block custom-input"
              />
            </div>

            <div className="border-b-2 border-tier-secondary pb-4"></div>

            <div className="m-4">
              <h2 className="text-xl font-bold mb-4">To</h2>

              {/* Client Selection Dropdown */}
              <div className="mb-4">
                <label htmlFor="client-select" className="block text-sm mb-2">
                  Select Client
                </label>

                <Select
                  id="client-select"
                  value={selectedOption}
                  onChange={handleClientSelect}
                  options={clientOptions}
                  styles={selectStyles}
                  classNamePrefix="react-select"
                  isSearchable={true}
                  placeholder="Select client..."
                  noOptionsMessage={() => "No clients found"}
                  loadingMessage={() => "Loading clients..."}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label htmlFor="to-name" className="block text-sm">
                    Name
                  </label>
                  <input
                    id="to-name"
                    type="text"
                    value={formData.to.name || ""}
                    onChange={(e) => handleChange(e, "to", "name")}
                    className="w-full custom-input"
                    placeholder="Enter name"
                  />
                </div>

                <div>
                  <label htmlFor="to-tinNo" className="block text-sm">
                    TIN
                  </label>
                  <input
                    id="to-tinNo"
                    type="text"
                    value={formData.to.tinNo || ""}
                    onChange={(e) => handleChange(e, "to", "tinNo")}
                    className="w-full custom-input"
                    placeholder="Enter TIN"
                  />
                </div>

                <div>
                  <label htmlFor="to-representative" className="block text-sm">
                    Representative
                  </label>
                  <input
                    id="to-representative"
                    type="text"
                    value={formData.to.rep || ""}
                    onChange={(e) => handleChange(e, "to", "rep")}
                    className="w-full custom-input"
                    placeholder="Enter representative name"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="to-address" className="block text-sm">
                    Address
                  </label>
                  <input
                    id="to-address"
                    type="text"
                    value={formData.to.address || ""}
                    onChange={(e) => handleChange(e, "to", "address")}
                    className="w-full custom-input"
                    placeholder="Enter address"
                  />
                </div>

                <div>
                  <label htmlFor="to-contact" className="block text-sm">
                    Contact No.
                  </label>
                  <input
                    id="to-contact"
                    type="text"
                    value={formData.to.contact || ""}
                    onChange={(e) => handleChange(e, "to", "contact")}
                    className="w-full custom-input"
                    placeholder="Enter contact details"
                  />
                </div>

                <div>
                  <label htmlFor="to-email" className="block text-sm">
                    Email
                  </label>
                  <input
                    id="to-email"
                    type="text"
                    value={formData.to.email || ""}
                    onChange={(e) => handleChange(e, "to", "email")}
                    className="w-full custom-input"
                    placeholder="Enter email address"
                  />
                </div>
              </div>
            </div>

            {/* Letter Opening */}
            {ackType !== "sec-res" && (
              <>
                <div className="border-b-2 border-tier-secondary pb-4"></div>
                <div className="m-4 flex-1">
                  <label className="block text-xs ">Salutation</label>
                  <input
                    type="text"
                    value={formData.letterOpening || ""}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        letterOpening: e.target.value,
                      }))
                    }
                    placeholder="e.g., Dear Mr. Smith"
                    className="custom-input"
                  />
                </div>
                {/* Undertaking */}
                <div className="m-4">
                  <div>
                    <label htmlFor="body1" className="block text-xs mb-2">
                      Opening
                    </label>
                    {!isNew && !isDataLoaded ? (
                      <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse flex items-center justify-center">
                        <span className="text-sm text-gray-500">
                          Loading content...
                        </span>
                      </div>
                    ) : (
                      <>
                        {isDataLoaded && (
                          <ReactQuill
                            id="body1"
                            key={`body1-${id || "new"}`}
                            value={formData.body1 || ""}
                            onChange={(value) =>
                              handleRichTextChange("body1", value)
                            }
                            modules={quillModules}
                            placeholder="Enter first section paragraph/s before the table..."
                            theme="snow"
                            className="bg-white/10 rounded-lg"
                          />
                        )}
                      </>
                    )}
                  </div>
                </div>

                {/* Items Table */}
                <TableConfigSection
                  tableConfig={tableConfig}
                  setTableConfig={setTableConfig}
                  invoiceType={invoiceType}
                  formData={formData}
                  setFormData={setFormData}
                />

                {/* Dynamic Table */}
                <DynamicTable
                  formData={formData}
                  setFormData={setFormData}
                  tableConfig={tableConfig}
                />

                {/* Additional Text after Table */}
                <div className="m-4">
                  <label htmlFor="body2" className="block text-xs mb-2">
                    Closing (Optional)
                  </label>
                  {!isNew && !isDataLoaded ? (
                    <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded animate-pulse flex items-center justify-center">
                      <span className="text-sm text-gray-500">
                        Loading content...
                      </span>
                    </div>
                  ) : (
                    <>
                      {isDataLoaded && (
                        <ReactQuill
                          id="body2"
                          key={`body2-${id || "new"}`}
                          value={formData.body2 || ""}
                          onChange={(value) =>
                            handleRichTextChange("body2", value)
                          }
                          modules={quillModules}
                          placeholder="Enter second section paragraph/s after the table..."
                          theme="snow"
                          className="bg-white/10 rounded-lg"
                        />
                      )}
                    </>
                  )}
                </div>

                <div className="border-b-2 border-tier-secondary pb-4"></div>

                {/* Formal Closing Section */}
                <div className="m-4">
                  <h2 className="text-md font-bold mb-4">Formal Closing</h2>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="sm:col-span-3">
                      <label htmlFor="complimentary" className="block text-xs">
                        Complimentary Closing
                      </label>
                      <input
                        id="complimentary"
                        type="text"
                        value={formData.closing.complimentary}
                        onChange={(e) =>
                          handleClosingChange(e, "complimentary")
                        }
                        className="w-full custom-input px-4 py-2 border rounded"
                        placeholder="Enter complementary closing"
                      />
                    </div>
                    <div>
                      <label htmlFor="closingName" className="block text-xs">
                        Name
                      </label>
                      <input
                        id="closingName"
                        type="text"
                        value={formData.closing.name}
                        onChange={(e) => handleClosingChange(e, "name")}
                        className="w-full custom-input px-4 py-2 border rounded"
                        placeholder="Enter your name"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="closingPosition"
                        className="block text-xs"
                      >
                        Position
                      </label>
                      <input
                        id="closingPosition"
                        type="text"
                        value={formData.closing.position}
                        onChange={(e) => handleClosingChange(e, "position")}
                        className="w-full custom-input px-4 py-2 border rounded"
                        placeholder="Enter your position"
                      />
                    </div>
                    <div>
                      <label htmlFor="closingCompany" className="block text-xs">
                        Company
                      </label>
                      <input
                        id="closingCompany"
                        type="text"
                        value={formData.closing.company}
                        onChange={(e) => handleClosingChange(e, "company")}
                        className="w-full custom-input px-4 py-2 border rounded"
                        placeholder="Enter your company"
                      />
                    </div>
                  </div>
                </div>
              </>
            )}

            {(invoiceType === "ack" || invoiceType === "secres") &&
              ackType &&
              ackType !== "non-notarized" && (
                <NotarizationSection
                  formData={formData}
                  ackType={ackType}
                  handleNotarizationChange={handleNotarizationChange}
                  handleNotaryInfoChange={handleNotaryInfoChange}
                  handleAffiantChange={handleAffiantChange}
                  handleWitnessChange={handleWitnessChange}
                  addAffiant={addAffiant}
                  removeAffiant={removeAffiant}
                  addWitness={addWitness}
                  removeWitness={removeWitness}
                  setFormData={setFormData}
                />
              )}
          </form>
        </div>
      </div>
    </>
  );
};

export default AdditionalInvoiceTypeForm;
